import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';

import { logger } from '@zep/core-logger';

import type { CreateBoardReq } from '@/api/gen/_model/createBoardReq';
import { useCreateBoard } from '@/api/gen/board-controller/board-controller.gen';
import { HttpStatusCodeEnum, ZepQuizApiError } from '@/api/httpClient';
import { useUser } from '@/lib/auth';
import {
  BLANK_BOARD_BACKGROUND,
  getRandomBoardEmoji,
} from '@/shared/features/board/constants';
import type { BoardTemplate } from '@/shared/features/board/types/template';
import {
  BOARD_ENTRY_FROM,
  getBoardPath,
} from '@/shared/features/board/utils/boardEntryRoute';
import { templateToCreateBoardReq } from '@/shared/features/board/utils/templateToCreateBoardReq';
import { useAutoLinkToClassroom } from '@/shared/features/classroom/hooks';

const EMPTY_GROUP_COUNT = 3;

// 생성 트래킹 메타: 어떤 방식(바로시작/템플릿)으로 어떤 구조의 보드를 만들었나.
type CreateTrackMeta = {
  createType: 'blank' | 'template';
  boardType: BoardTemplate['type'];
  templateId?: string;
};

export function useCreateBoardFromTemplate() {
  const { t } = useTranslation('board');
  const { t: tTemplate } = useTranslation('boardTemplates');
  const router = useRouter();
  const { user } = useUser();
  const createBoard = useCreateBoard();
  const { autoLink } = useAutoLinkToClassroom();

  /**
   * 학급 콘텐츠 연결 모달의 "새 보드 만들기"로 진입했을 때만 실린다.
   * 이 값이 있으면 생성 완료 시 해당 학급으로 자동 연결한다.
   * (마이보드·/board/new 직접 진입은 값이 없어 기존과 동일하게 동작)
   */
  const classroomId =
    typeof router.query.classroomId === 'string'
      ? router.query.classroomId
      : undefined;

  const createBoardAndNavigate = async (
    payload: CreateBoardReq,
    meta: CreateTrackMeta,
  ) => {
    // KPI 유저타입 분포(TEACHER/STUDENT/EMPTY)와 매핑. 비로그인은 'empty'.
    const role = user ? user.type : 'empty';
    const trackBase = {
      create_type: meta.createType,
      board_type: meta.boardType,
      role,
      ...(meta.templateId ? { template_id: meta.templateId } : {}),
    };

    // 클라에서 먼저 차단: 비로그인은 서버 왕복 없이 바로 로그인으로 보낸다.
    // (서버 403/401 fallback은 로그인 유저가 만료 등으로 거부당하는 경우의 이중 방어)
    if (!user) {
      void router.push({
        pathname: '/login',
        query: { callbackUrl: router.asPath },
      });
      return;
    }

    let boardPath: string;

    try {
      const result = await createBoard.mutateAsync({ data: payload });

      // 완료 계측: 기존 enter_create(진입)를 대체하는 '주간 생성 보드 수' 소스.
      logger.track('board_create_success', {
        board_id: result.data.id,
        ...trackBase,
      });

      boardPath = getBoardPath(result.data.id, {
        from: classroomId
          ? BOARD_ENTRY_FROM.CLASSROOM
          : BOARD_ENTRY_FROM.MY_BOARD,
      });

      // 학급 맥락으로 진입했으면 이동 전에 연결까지 끝낸다. 이동 후 연결하면
      // 화면이 바뀌는 도중 토스트가 떠 어느 화면의 결과인지 읽기 어렵다.
      // autoLink 는 실패해도 throw 하지 않으므로 이동이 막히지 않는다
      // (보드는 이미 생성됨 — 연결 실패는 토스트로 알리고 수동 연결을 유도).
      if (classroomId) {
        await autoLink({
          classroomId,
          contentId: result.data.id,
          contentType: 'BOARD',
        });
      }
    } catch (error) {
      // 템플릿 콘텐츠(제목/그룹명/공지)가 서버 한도를 넘으면 여기서 조용히 실패한다.
      // 무증상 삼킴을 막기 위해 최소한 콘솔에 남긴다.
      logger.track('board_create_fail', {
        ...trackBase,
        fail_reason: error instanceof Error ? error.message : 'unknown',
      });
      console.error('[board] createBoardFromTemplate failed', error);
      // 비로그인/미인증이 생성 시도 → 서버가 403/401 → 로그인으로 보낸다.
      // 헤더 로그인과 동일하게 callbackUrl(현재 경로)을 남겨 로그인 후 복귀시킨다.
      if (
        error instanceof ZepQuizApiError &&
        (error.httpStatus === HttpStatusCodeEnum.FORBIDDEN_403 ||
          error.httpStatus === HttpStatusCodeEnum.UNAUTHORIZED_401)
      ) {
        void router.push({
          pathname: '/login',
          query: { callbackUrl: router.asPath },
        });
      }
      return;
    }

    try {
      await router.push(boardPath);
    } catch (error) {
      if ((error as { cancelled?: boolean }).cancelled) return;
      await router.replace(boardPath).catch(() => false);
    }
  };

  const getDefaultTitle = () =>
    user?.username
      ? t('board.header.title.default', { username: user.username })
      : t('board.header.title.new');

  const buildEmptyGroups = (): NonNullable<CreateBoardReq['groups']> =>
    Array.from({ length: EMPTY_GROUP_COUNT }, (_, i) => ({
      name: t('board.group.defaultName', { number: i + 1 }),
    }));

  const createBlankBoard = async (type: BoardTemplate['type']) =>
    createBoardAndNavigate(
      {
        title: getDefaultTitle(),
        emoji: getRandomBoardEmoji(),
        background: BLANK_BOARD_BACKGROUND[type],
        groups: type === 'GROUP' ? buildEmptyGroups() : [],
        notices: [],
        ...(type === 'TIMELINE' ? { layout: 'TIMELINE' as const } : {}),
      },
      { createType: 'blank', boardType: type },
    );

  const createFromTemplate = async (template: BoardTemplate) =>
    createBoardAndNavigate(templateToCreateBoardReq(template, tTemplate), {
      createType: 'template',
      boardType: template.type,
      templateId: template.id,
    });

  return {
    createBlankBoard,
    createFromTemplate,
    // isPending은 mutation resolve 직후 false로 풀리지만, 이어지는 router.push가
    // 완료되기 전까지 이 화면이 살아있다. 그 공백에서 다른 템플릿을 다시 눌러
    // 보드가 2개 생성되는 것을 막기 위해 isSuccess까지 포함한다(성공 시 곧 언마운트).
    isCreating: createBoard.isPending || createBoard.isSuccess,
  };
}
